import { environment } from 'src/environments/environment';

/**
 * Branding for the pre-login pages (login, register) from the whitelabel details
 * response (organization/whitelabel/details). The theme colours are mapped onto the
 * --bk-* palette those pages are styled with (src/index.html), set on the page's host
 * element so the landing page keeps its own colours.
 *
 * Roles follow the logged-in theme (layouts/rightsidebar/theme-style.ts):
 *  - customer_journey_*: the main action (Sign in, Submit) and accents
 *  - general_button_*:   the dark brand panel and its text
 * Neutrals (body text, lines, page background) stay fixed, as in the logged-in theme.
 */
export function whitelabelThemeVars(theme: any): Record<string, string> {
  const color = (value: any): string | null =>
    typeof value === 'string' && value.trim() && CSS.supports('color', value.trim()) ? value.trim() : null;
  const mix = (a: string, percent: number, b: string) => `color-mix(in srgb, ${a} ${percent}%, ${b})`;
  const vars: Record<string, string> = {};

  const cta = color(theme?.customer_journey_button_first_color);
  if (cta) {
    vars['--bk-pin'] = cta;
    vars['--bk-pin-ink'] = color(theme?.customer_journey_button_second_color) ?? cta;
    vars['--bk-pin-2'] = mix(cta, 80, '#fff');
    vars['--bk-pin-3'] = mix(cta, 65, '#fff');
    vars['--bk-pin-wash'] = mix(cta, 14, '#fff');
  }
  const onCta = color(theme?.customer_journey_text_color);
  if (onCta) vars['--bk-on-pin'] = onCta;

  const primary = color(theme?.general_button_first_color);
  if (primary) {
    const deep = color(theme?.general_button_second_color) ?? primary;
    const onPrimary = color(theme?.general_button_text_color) ?? '#fff';
    vars['--bk-hero'] = deep;
    vars['--bk-hero-2'] = primary;
    vars['--bk-hero-ink'] = onPrimary;
    vars['--bk-hero-dim'] = mix(onPrimary, 62, deep);
    vars['--bk-hero-line'] = mix(onPrimary, 16, primary);
    vars['--bk-hero-card'] = mix(onPrimary, 7, deep);
  }
  return vars;
}

export function applyWhitelabelTheme(host: HTMLElement, theme: any) {
  Object.entries(whitelabelThemeVars(theme)).forEach(([name, value]) => host.style.setProperty(name, value));
}

/** organization.code, trimmed; '' when the response has none. */
export function whitelabelOrgCode(details: any): string {
  const code = details?.organization?.code ?? details?.data?.organization?.code;
  return code == null ? '' : String(code).trim();
}

/** True for Biyaheko's own site: the whitelabel details carry no organization.code. */
export function whitelabelIsByk(details: any): boolean {
  return whitelabelOrgCode(details) === '';
}

/** Company name to show: Biyaheko on its own site, otherwise organization.organization_name. */
export function whitelabelCompanyName(details: any): string {
  const name = details?.organization?.organization_name ?? details?.data?.organization?.organization_name;
  return whitelabelIsByk(details) || !name?.trim() ? 'Biyaheko' : name.trim();
}

export interface WhitelabelContact { icon: 'mail' | 'phone' | 'pin'; title: string; text: string; href?: string; }

/** "Get in touch" entries from organization (email, phone, office); empty ones are left out. */
export function whitelabelContacts(details: any): WhitelabelContact[] {
  const org = details?.organization ?? details?.data?.organization;
  const clean = (value: any) => (typeof value === 'string' ? value.trim() : '');
  const email = clean(org?.support_email);
  const phone = clean(org?.support_phone);
  const office = [org?.address, org?.city, org?.country_name].map(clean).filter(Boolean).join(', ');
  const contacts: WhitelabelContact[] = [];
  if (email) contacts.push({ icon: 'mail', title: 'Email us', text: email, href: `mailto:${email}` });
  if (phone) contacts.push({ icon: 'phone', title: 'Call us', text: phone, href: `tel:${phone.replace(/[^\d+]/g, '')}` });
  if (office) contacts.push({ icon: 'pin', title: 'Visit us', text: office });
  return contacts;
}

/** organization.profile_picture as an image URL (relative paths sit under imageUrl), or null. */
export function whitelabelLogo(details: any): string | null {
  const picture = details?.organization?.profile_picture;
  if (typeof picture !== 'string' || !picture.trim()) return null;
  return /^(https?:|data:|\/\/)/i.test(picture) ? picture : `${environment.imageUrl}${picture.replace(/^\//, '')}`;
}
